import type { ITopicEntity } from '@1js/cortex-topics-types';
import { moveToFulfilled, remove, update } from '@ms/yammer-data-support';
import type { Reducer } from 'redux';

import type {
  CortexTopicsReceivedAction,
  OfficeTopicLoadAuthorizedFulfilledAction,
} from '../../domains/cortexTopics/actions.types';
import type {
  TopicsAllLoadFulfilledAction,
  TopicsAllLoadMoreFulfilledAction,
} from '../../domains/engageTopicsAll/actions.types';
import type {
  FeedLoadFulfilledAction,
  FeedLoadNewerThreadsFulfilledAction,
  FeedLoadOlderThreadsFulfilledAction,
} from '../../domains/feedLoad/actions.types';
import type {
  NestedThreadDeepLinkSecondLevelLoadFulfilledAction,
  NestedThreadDeepLinkTopLevelLoadFulfilledAction,
  NestedThreadLoadFulfilledAction,
} from '../../domains/nestedThread/types';
import type { NestedThreadListLoadFulfilledAction } from '../../domains/nestedThreadList/actions.types';
import type {
  ThreadPinToEventFeedFulfilledAction,
  ThreadPinToGroupFeedFulfilledAction,
  ThreadPinToTeamsMeetingFeedFulfilledAction,
  ThreadUnpinFromEventFeedFulfilledAction,
  ThreadUnpinFromGroupFeedFulfilledAction,
  ThreadUnpinFromTeamsMeetingFeedFulfilledAction,
} from '../../domains/pinnedThreads/actions.types';
import type { ExtractSuggestionsFulfilledAction } from '../../domains/publisherSuggestions/types';
import type {
  PublishDirectMessageFulfilledAction,
  PublishEditMessageFulfilledAction,
  PublishEventMessageFulfilledAction,
  PublishGroupMessageFulfilledAction,
  PublishNetworkQuestionMessageFulfilledAction,
  PublishPollMessageFulfilledAction,
  PublishPraiseMessageFulfilledAction,
  PublishReplyMessageFulfilledAction,
  PublishUserMessageFulfilledAction,
  PublishUserPollMessageFulfilledAction,
  PublishUserPraiseMessageFulfilledAction,
} from '../../domains/publishers/actions.types';
import type { SearchLoadFulfilledAction, SearchLoadMoreFulfilledAction } from '../../domains/searches/actions.types';
import type {
  SecondLevelRepliesLoadLastFulfilledAction,
  SecondLevelRepliesLoadNextFulfilledAction,
  SecondLevelRepliesLoadPreviousFulfilledAction,
} from '../../domains/secondLevelReplies/actions.types';
import type {
  SuggestedTopicsLoadFulfilledAction,
  SuggestedTopicsLoadMoreFulfilledAction,
} from '../../domains/suggestedTopics/actions.types';
import type { ThreadSharesLoadFulfilledAction } from '../../domains/threadShares/actions.types';
import type { ThreadsAppliedTopicNamesLoadFulfilledAction } from '../../domains/threadsAppliedTopicNames/actions.types';
import type {
  TopLevelRepliesLoadInitialFulfilledAction,
  TopLevelRepliesLoadLastFulfilledAction,
  TopLevelRepliesLoadNextFulfilledAction,
  TopLevelRepliesLoadPreviousFulfilledAction,
} from '../../domains/topLevelReplies/actions.types';
import type {
  TopicHeaderLoadAction,
  TopicHeaderLoadFulfilledAction,
} from '../../domains/topicHeaderLoad/actions.types';
import type {
  AddThreadTopicFulfilledAction,
  RemoveTopicFulfilledAction,
  TopicCreateFulfilledAction,
  TopicEditFulfilledAction,
  TopicFetchAction,
  TopicFetchByIdAction,
} from '../../domains/topics/actions.types';
import type { RemoveTopicFulfilledProps } from '../../domains/topics/removeTopicActions';
import type { TopicsBulkDeleteFulfilledAction } from '../../domains/topicsBulkDelete/actions.types';
import type {
  TopicsFollowedLoadFulfilledAction,
  TopicsFollowedLoadMoreFulfilledAction,
} from '../../domains/topicsFollowedLoad/actions.types';
import type { TypeaheadFulfilledAction } from '../../domains/typeahead/actions.types';

import type { Topic, Topics } from './types';

export type TopicAction =
  | ExtractSuggestionsFulfilledAction
  | FeedLoadFulfilledAction
  | FeedLoadNewerThreadsFulfilledAction
  | FeedLoadOlderThreadsFulfilledAction
  | SearchLoadFulfilledAction
  | SearchLoadMoreFulfilledAction
  | ThreadPinToEventFeedFulfilledAction
  | ThreadPinToGroupFeedFulfilledAction
  | ThreadPinToTeamsMeetingFeedFulfilledAction
  | ThreadUnpinFromEventFeedFulfilledAction
  | ThreadUnpinFromGroupFeedFulfilledAction
  | ThreadUnpinFromTeamsMeetingFeedFulfilledAction
  | OfficeTopicLoadAuthorizedFulfilledAction
  | PublishEventMessageFulfilledAction
  | PublishGroupMessageFulfilledAction
  | PublishUserMessageFulfilledAction
  | PublishUserPollMessageFulfilledAction
  | PublishUserPraiseMessageFulfilledAction
  | PublishDirectMessageFulfilledAction
  | PublishEditMessageFulfilledAction
  | PublishReplyMessageFulfilledAction
  | PublishPollMessageFulfilledAction
  | PublishPraiseMessageFulfilledAction
  | PublishNetworkQuestionMessageFulfilledAction
  | TypeaheadFulfilledAction
  | TopicFetchAction
  | TopicFetchByIdAction
  | TopicHeaderLoadAction
  | TopicsFollowedLoadFulfilledAction
  | TopicsFollowedLoadMoreFulfilledAction
  | AddThreadTopicFulfilledAction
  | TopicHeaderLoadFulfilledAction
  | NestedThreadLoadFulfilledAction
  | NestedThreadDeepLinkTopLevelLoadFulfilledAction
  | NestedThreadDeepLinkSecondLevelLoadFulfilledAction
  | NestedThreadListLoadFulfilledAction
  | TopLevelRepliesLoadInitialFulfilledAction
  | TopLevelRepliesLoadLastFulfilledAction
  | TopLevelRepliesLoadPreviousFulfilledAction
  | TopLevelRepliesLoadNextFulfilledAction
  | SecondLevelRepliesLoadPreviousFulfilledAction
  | SecondLevelRepliesLoadNextFulfilledAction
  | TopicCreateFulfilledAction
  | TopicsAllLoadFulfilledAction
  | TopicsAllLoadMoreFulfilledAction
  | SuggestedTopicsLoadFulfilledAction
  | SuggestedTopicsLoadMoreFulfilledAction
  | TopicEditFulfilledAction
  | SecondLevelRepliesLoadLastFulfilledAction
  | ThreadSharesLoadFulfilledAction
  | CortexTopicsReceivedAction
  | ThreadsAppliedTopicNamesLoadFulfilledAction
  | RemoveTopicFulfilledAction
  | TopicsBulkDeleteFulfilledAction;

const initialState: Topics = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const stateWithFulfilledTopics = (state: Topics, newTopics: Record<Topic['id'], Topic> | undefined) => {
  if (!newTopics) {
    return state;
  }

  return Object.keys(newTopics).reduce((newState, topicId) => {
    const existingTopic = newState.fulfilled.byId[topicId];

    const payloadTopic: Topic = newTopics[topicId];
    const existingTopicHasName = existingTopic?.name != null;
    const existingTopicNeedsDescription =
      existingTopic && !existingTopic.description && payloadTopic.description != null;
    const existingTopicNeedsShortDescription =
      existingTopic && !existingTopic.shortDescription && payloadTopic.shortDescription != null;
    const existingTopicNeedsViewerCanEdit =
      existingTopic && existingTopic.viewerCanEdit == null && payloadTopic.viewerCanEdit != null;
    const existingTopicNeedsViewerCanDelete =
      existingTopic && existingTopic.viewerCanDelete == null && payloadTopic.viewerCanDelete != null;
    const payloadTopicIsMissingAuthToken = !payloadTopic.officeTopicAuthorizationToken;

    if (
      existingTopicHasName &&
      payloadTopicIsMissingAuthToken &&
      !existingTopicNeedsDescription &&
      !existingTopicNeedsShortDescription &&
      !existingTopicNeedsViewerCanEdit &&
      !existingTopicNeedsViewerCanDelete
    ) {
      return newState;
    }

    return moveToFulfilled(newState, {
      [topicId]: payloadTopic,
    });
  }, state);
};

const updateTopicsWithName = (state: Topics, action: CortexTopicsReceivedAction) => {
  const fulfilledTopics = state.fulfilled.byId;
  const officeTopicsPayload = action.payload;

  type OfficeTopicsById = Record<ITopicEntity['Id'], ITopicEntity>;

  // Cortex topics can mutate as they are merged and updated by the SDK.  This means
  // that the SDK will return a desired topic based on the client request and return
  // that topic with a different officeTopicId.  This is a temporary fix until the
  // SDK can update its API.

  const officeTopicsByAncestorId: OfficeTopicsById = officeTopicsPayload.reduce((topicsByAncestorId, topic) => {
    if (!topic.AncestorIds?.length) {
      return topicsByAncestorId;
    }

    const topicByAncestorIds = topic.AncestorIds.reduce(
      (topicByAncestorIds, ancestorId) => ({ ...topicByAncestorIds, [ancestorId]: topic }),
      {}
    );

    return { ...topicsByAncestorId, ...topicByAncestorIds };
  }, {});

  const officeTopicsPayloadById: OfficeTopicsById = officeTopicsPayload.reduce(
    (officeTopicsById, topic) => ({ ...officeTopicsById, [topic.Id]: topic }),
    {}
  );

  return Object.keys(fulfilledTopics).reduce((newState, topicId) => {
    const { name, officeTopicId } = fulfilledTopics[topicId];

    if (!officeTopicId) {
      return newState;
    }

    const officeTopicAncestor = officeTopicsByAncestorId[officeTopicId];
    const officeTopicPayload = officeTopicsPayloadById[officeTopicId];

    if (officeTopicPayload && !name) {
      return update(newState, topicId, {
        name: officeTopicPayload.DisplayName,
      });
    }

    if (officeTopicAncestor && !name) {
      return update(newState, topicId, {
        name: officeTopicAncestor.DisplayName,
        officeTopicId: officeTopicAncestor.Id,
      });
    }

    if (!name) {
      return remove(newState, [topicId]);
    }

    return newState;
  }, state);
};

const removeTopic = (state: Topics, topicId: RemoveTopicFulfilledProps) => {
  const curatedTopicId = topicId?.curatedTopicId;

  return curatedTopicId ? remove(state, [curatedTopicId]) : state;
};

// eslint-disable-next-line max-lines-per-function
const reducer: Reducer<Topics, TopicAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'FEED.LOAD_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'FEED.LOAD_NEWER_THREADS_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'FEED.LOAD_OLDER_THREADS_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'SEARCH.LOAD_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'SEARCH.LOAD_MORE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'THREAD.PIN_TO_EVENT_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'THREAD.PIN_TO_GROUP_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'THREAD.PIN_TO_TEAMS_MEETING_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'THREAD.UNPIN_FROM_EVENT_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'THREAD.UNPIN_FROM_GROUP_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'THREAD.UNPIN_FROM_TEAMS_MEETING_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'PUBLISHER.EXTRACT_SUGGESTIONS_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'PUBLISHER.PUBLISH_EVENT_MESSAGE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'PUBLISHER.PUBLISH_GROUP_MESSAGE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'PUBLISHER.PUBLISH_USER_MESSAGE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'PUBLISHER.PUBLISH_USER_POLL_MESSAGE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'PUBLISHER.PUBLISH_USER_PRAISE_MESSAGE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'PUBLISHER.PUBLISH_DIRECT_MESSAGE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'PUBLISHER.PUBLISH_EDIT_MESSAGE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'PUBLISHER.PUBLISH_REPLY_MESSAGE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'PUBLISHER.PUBLISH_POLL_MESSAGE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'PUBLISHER.PUBLISH_PRAISE_MESSAGE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'PUBLISHER.PUBLISH_NETWORK_QUESTION_MESSAGE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TYPEAHEAD.LOAD_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TOPICS_ALL.LOAD_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TOPICS_ALL.LOAD_MORE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'SUGGESTED_TOPICS.LOAD_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'SUGGESTED_TOPICS.LOAD_MORE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TOPIC_HEADER.LOAD_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TOPICS_FOLLOWED.LOAD_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TOPICS_FOLLOWED.LOAD_MORE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'THREAD_TOPIC.ADD_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'NESTED_THREAD.LOAD_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_TOP_LEVEL_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'NESTED_THREAD.LOAD_DEEP_LINK_SECOND_LEVEL_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'NESTED_THREAD_LIST.LOAD_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TOP_LEVEL_REPLIES.LOAD_INITIAL_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TOP_LEVEL_REPLIES.LOAD_PREVIOUS_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TOP_LEVEL_REPLIES.LOAD_NEXT_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TOP_LEVEL_REPLIES.LOAD_LAST_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'SECOND_LEVEL_REPLIES.LOAD_PREVIOUS_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'SECOND_LEVEL_REPLIES.LOAD_NEXT_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TOPIC.CREATE_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TOPIC.EDIT_FULFILLED':
      return update(stateWithFulfilledTopics(state, action.payload.entities.topics), action.payload.topicId, {
        description: action.payload.topic.description,
      });
    case 'OFFICE_TOPIC.LOAD_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'SECOND_LEVEL_REPLIES.LOAD_LAST_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'THREAD_SHARES.LOAD_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TOPIC.FETCH_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TOPIC.FETCH_BY_ID_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'OFFICE_TOPICS.CORTEX_TOPICS_RECEIVED':
      return updateTopicsWithName(state, action);
    case 'THREADS_APPLIED_TOPIC_NAMES.LOAD_FULFILLED':
      return stateWithFulfilledTopics(state, action.payload.entities.topics);
    case 'TOPIC.REMOVE_FULFILLED':
      return removeTopic(state, action.payload);
    case 'TOPICS.BULK_DELETE_FULFILLED':
      return remove(state, action.payload.topicIds);
    default:
      return state;
  }
};

export default reducer;
